import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import EventHeader from './components/EventHeader';
import EventDetails from './components/EventDetails';
import SpeakerProfiles from './components/SpeakerProfiles';
import RegistrationModal from './components/RegistrationModal';
import RelatedEvents from './components/RelatedEvents';
import { eventService } from '../../utils/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const EventDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [error, setError] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/city-official-dashboard' },
    { label: 'Events', path: '/events' },
    { label: event?.title || 'Event Details' }
  ];

  useEffect(() => {
    fetchEventDetails();
    fetchRelatedEvents();
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: eventError } = await eventService?.getEvent(id);
      
      if (eventError) {
        setError('Failed to load event details');
        return;
      }
      
      setEvent(data);
      
      // Check if user is already registered
      if (user) {
        const { data: registrations } = await eventService?.getUserEventRegistrations(user?.id);
        const isAlreadyRegistered = registrations?.some(reg => reg?.event?.id === id);
        setIsRegistered(isAlreadyRegistered);
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedEvents = async () => {
    try {
      const { data, error: eventsError } = await eventService?.getAllEvents({
        event_type: 'workshop',
        upcoming: true
      });
      
      if (!eventsError && data) {
        // Filter out current event and limit to 3
        const filtered = data?.filter(e => e?.id !== id)?.slice(0, 3);
        setRelatedEvents(filtered);
      }
    } catch (error) {
      console.error('Failed to load related events:', error);
    }
  };

  const handleRegister = async () => {
    if (!user) {
      navigate('/user-login');
      return;
    }

    if (!event?.registration_required) {
      setShowRegistrationModal(true);
      return;
    }

    try {
      setRegistering(true);
      const { error: registrationError } = await eventService?.registerForEvent(id, user?.id);
      
      if (registrationError) {
        if (registrationError?.message?.includes('duplicate')) {
          setError('You are already registered for this event.');
        } else {
          setError('Registration failed. Please try again.');
        }
        return;
      }
      
      setIsRegistered(true);
      setShowRegistrationModal(true);
    } catch (error) {
      setError('Registration failed. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!user) return;

    try {
      setRegistering(true);
      const { error: cancelError } = await eventService?.cancelEventRegistration(id, user?.id);
      
      if (cancelError) {
        setError('Failed to cancel registration. Please try again.');
        return;
      }
      
      setIsRegistered(false);
    } catch (error) {
      setError('Failed to cancel registration. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 lg:px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading event details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-6 lg:px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Event Not Found</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate(-1)}>Go Back</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const isUpcoming = event && new Date(event.start_date) > new Date();
  const isPastDeadline = event?.registration_deadline && new Date(event.registration_deadline) < new Date();
  const isFull = event?.max_participants && event?.current_participants >= event?.max_participants;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 lg:px-6">
        <div className="mb-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-destructive text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <EventHeader
              event={event}
              isRegistered={isRegistered}
              registering={registering}
              onRegister={handleRegister}
              onCancelRegistration={handleCancelRegistration}
              isUpcoming={isUpcoming}
              isPastDeadline={isPastDeadline}
              isFull={isFull}
            />

            <EventDetails event={event} />

            {event?.agenda && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                  <Icon name="Calendar" size={20} />
                  <span>Event Agenda</span>
                </h3>
                <div className="prose prose-sm max-w-none">
                  {typeof event?.agenda === 'string' ? (
                    <p className="text-muted-foreground">{event?.agenda}</p>
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {JSON.stringify(event?.agenda, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            )}

            <SpeakerProfiles event={event} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Info Card */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-semibold mb-4">Event Information</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Calendar" size={16} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      {format(new Date(event?.start_date), 'MMMM dd, yyyy')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(event?.start_date), 'h:mm a')} - 
                      {event?.end_date && format(new Date(event?.end_date), ' h:mm a')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Icon name={event?.is_virtual ? "Monitor" : "MapPin"} size={16} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      {event?.is_virtual ? 'Virtual Event' : event?.location}
                    </p>
                    {event?.is_virtual && event?.virtual_link && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => window.open(event?.virtual_link, '_blank')}
                      >
                        Join Virtual Event
                      </Button>
                    )}
                  </div>
                </div>

                {event?.max_participants && (
                  <div className="flex items-start space-x-3">
                    <Icon name="Users" size={16} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Capacity</p>
                      <p className="text-xs text-muted-foreground">
                        {event?.current_participants} / {event?.max_participants} registered
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <Icon name="User" size={16} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Organizer</p>
                    <p className="text-xs text-muted-foreground">
                      {event?.organizer?.full_name}
                    </p>
                    {event?.organizer?.organization && (
                      <p className="text-xs text-muted-foreground">
                        {event?.organizer?.organization}
                      </p>
                    )}
                  </div>
                </div>

                {event?.tags && event?.tags?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {event?.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Share Event */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-semibold mb-4">Share Event</h3>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const text = `Check out this event: ${event?.title}`;
                    const url = window.location?.href;
                    if (navigator.share) {
                      navigator.share({ title: event?.title, text, url });
                    } else {
                      navigator.clipboard?.writeText(`${text} ${url}`);
                    }
                  }}
                >
                  <Icon name="Share2" size={14} />
                  Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const url = window.location?.href;
                    navigator.clipboard?.writeText(url);
                  }}
                >
                  <Icon name="Copy" size={14} />
                  Copy Link
                </Button>
              </div>
            </div>

            <RelatedEvents events={relatedEvents} />
          </div>
        </div>

        {/* Registration Success Modal */}
        {showRegistrationModal && (
          <RegistrationModal
            event={event}
            isRegistered={isRegistered}
            onClose={() => setShowRegistrationModal(false)}
          />
        )}
      </main>
    </div>
  );
};

export default EventDetailsPage;